/**
 * PasswordResetConsumePage — token-consume side of the `/password-reset`
 * flow. The router-level dispatcher pulls `?token=…` from the URL and
 * passes it as a prop; this page validates the token format client-side,
 * collects the new password + confirm, and submits
 * `POST /auth/reset-password` (Public + global CSRF).
 *
 * Status mapping (controller at apps/api/src/modules/identity/interface/
 * auth.controller.ts:1010–1043):
 *
 *   204 No Content                            → success surface
 *   400 password_reset_token_invalid          → "invalid link" full-page
 *   400 password_policy                       → inline error on newPassword
 *   410 password_reset_token_expired|used     → "invalid link" full-page
 *   429                                       → submit-error region
 *   5xx                                       → "temporarily unavailable"
 *   network failure                           → "network error"
 *
 * Anti-enum FE policy: all token-rejection states (client-side bad
 * format, BE 400, BE 410-expired, BE 410-used) collapse to one
 * byte-identical "invalid link" surface. The BE differentiates today
 * via distinct 400/410 codes — this page refuses to amplify that
 * leak. Whatever the reason, the user can only take one action
 * ("request a new link"), so revealing the cause carries no
 * actionable UX value and gives an interceptor of the email link a
 * free discriminator.
 *
 * The 64-hex token-format check reuses the canonical regex from
 * `resetPasswordSchema.shape.token` in `@artemis/validators` to avoid
 * drift. A malformed token short-circuits to the "invalid link" surface
 * without consuming the rate limit.
 *
 * confirmPassword is FE-only; the wire payload is strictly `{ token,
 * newPassword }` — picked from the canonical schema so the contract
 * cannot drift.
 *
 * Tokens-only colors via `@sparta/design-tokens` Tailwind preset and
 * the semantic CSS variables (`--bg-canvas`, `--fg-primary`, …).
 * Touch targets ≥ 44 px, `role="alert"` aria-live regions, semantic
 * landmarks per CLAUDE.md Frontend rule §7 (WCAG 2.1 AA).
 */

import * as React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetPasswordSchema } from '@artemis/validators';
import { Button, Card, TextField } from '@sparta/ui-kit';
import { apiClient } from '../../shared/api-client.js';

// FE-only schema: takes the canonical newPassword rule (12..128) from
// `@artemis/validators` as SSOT, adds a confirm-password field, and
// refines that the two passwords match. confirmPassword never leaves
// this component — the wire payload is shaped from `newPassword` +
// the prop-supplied `token`.
const consumeFormSchema = resetPasswordSchema
  .pick({ newPassword: true })
  .extend({ confirmPassword: z.string() })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ConsumeFormInput = z.infer<typeof consumeFormSchema>;

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'invalid-link' }
  | { kind: 'rate-limited' }
  | { kind: 'temporary-failure'; message: string }
  | { kind: 'password-policy'; message: string };

const ERROR_BODY_KEYS = {
  TOKEN_INVALID: 'password_reset_token_invalid',
  TOKEN_EXPIRED: 'password_reset_token_expired',
  TOKEN_USED: 'password_reset_token_already_used',
  PASSWORD_POLICY: 'password_policy',
} as const;

function readErrorCode(maybeError: unknown): string | undefined {
  if (typeof maybeError !== 'object' || maybeError === null) return undefined;
  const value = (maybeError as { error?: unknown }).error;
  return typeof value === 'string' ? value : undefined;
}

function readErrorMessage(maybeError: unknown): string | undefined {
  if (typeof maybeError !== 'object' || maybeError === null) return undefined;
  const value = (maybeError as { message?: unknown }).message;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export interface PasswordResetConsumePageProps {
  /**
   * Raw token from the email link's `?token=` query param. The page
   * validates the 64-hex format on mount and renders the "invalid
   * link" surface if it fails — no network call is made in that case.
   */
  token: string;
}

export function PasswordResetConsumePage({
  token,
}: PasswordResetConsumePageProps): React.ReactElement {
  // Token-format check reuses the canonical regex from the wire
  // schema, so the FE never drifts from the BE contract.
  const tokenFormatValid = React.useMemo(
    () => resetPasswordSchema.shape.token.safeParse(token).success,
    [token],
  );

  const [submitState, setSubmitState] = React.useState<SubmitState>(() =>
    tokenFormatValid ? { kind: 'idle' } : { kind: 'invalid-link' },
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConsumeFormInput>({
    resolver: zodResolver(consumeFormSchema),
    mode: 'onTouched',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values): Promise<void> => {
    setSubmitState({ kind: 'submitting' });
    try {
      const result = await apiClient.POST('/auth/reset-password', {
        body: { token, newPassword: values.newPassword },
      });
      const { response } = result;
      if (response.ok) {
        setSubmitState({ kind: 'success' });
        return;
      }

      const errorCode = readErrorCode(
        (result as { error?: unknown }).error,
      );
      const errorMessage = readErrorMessage(
        (result as { error?: unknown }).error,
      );

      if (response.status === 400) {
        if (errorCode === ERROR_BODY_KEYS.PASSWORD_POLICY) {
          setSubmitState({
            kind: 'password-policy',
            message:
              errorMessage ?? 'Password does not meet the security policy.',
          });
          return;
        }
        // Anything else 400 → token rejected by the server. Collapses
        // into the unified anti-enum "invalid link" surface.
        setSubmitState({ kind: 'invalid-link' });
        return;
      }
      if (response.status === 410) {
        // 410 expired or already-used — anti-enum: surface is
        // byte-identical to 400-invalid above. See header note.
        setSubmitState({ kind: 'invalid-link' });
        return;
      }
      if (response.status === 429) {
        setSubmitState({ kind: 'rate-limited' });
        return;
      }
      if (response.status >= 500) {
        setSubmitState({
          kind: 'temporary-failure',
          message:
            'Password reset is temporarily unavailable. Try again shortly.',
        });
        return;
      }
      setSubmitState({
        kind: 'temporary-failure',
        message: 'Something went wrong. Try again.',
      });
    } catch {
      setSubmitState({
        kind: 'temporary-failure',
        message: 'Network error. Check your connection and try again.',
      });
    }
  });

  const submitting = submitState.kind === 'submitting';

  return (
    <main
      className="min-h-screen w-full"
      style={{ background: 'var(--bg-canvas)', color: 'var(--fg-primary)' }}
    >
      <div className="mx-auto max-w-md px-4 py-12 sm:py-16">
        {submitState.kind === 'success' ? (
          <SuccessSurface />
        ) : submitState.kind === 'invalid-link' ? (
          <InvalidLinkSurface />
        ) : (
          <FormSurface
            register={register}
            errors={errors}
            onSubmit={onSubmit}
            submitState={submitState}
            submitting={submitting}
          />
        )}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

function FormSurface(props: {
  register: ReturnType<typeof useForm<ConsumeFormInput>>['register'];
  errors: ReturnType<
    typeof useForm<ConsumeFormInput>
  >['formState']['errors'];
  onSubmit: (event?: React.BaseSyntheticEvent) => Promise<void>;
  submitState: SubmitState;
  submitting: boolean;
}): React.ReactElement {
  const { register, errors, onSubmit, submitState, submitting } = props;

  // The BE policy message lives on the newPassword field when present;
  // client-side Zod errors take precedence (they fire before submit).
  const newPasswordError =
    errors.newPassword?.message ??
    (submitState.kind === 'password-policy' ? submitState.message : undefined);

  // Submit-time errors that keep the form mounted go in the aria-live
  // region below the fields. password-policy is surfaced inline above
  // and excluded here to avoid double-announcing.
  const submitErrorMessage: string | undefined =
    submitState.kind === 'rate-limited'
      ? 'Too many attempts. Please wait a moment and try again.'
      : submitState.kind === 'temporary-failure'
        ? submitState.message
        : undefined;

  return (
    <>
      <h1
        className="text-3xl font-bold"
        style={{ letterSpacing: '-0.6px' }}
      >
        Choose a new password
      </h1>
      <p
        className="mb-8 mt-2 text-sm"
        style={{ color: 'var(--fg-tertiary)' }}
      >
        Pick a strong password you don't use anywhere else. For your
        security, all active sessions will be signed out when the reset
        completes.
      </p>

      <form onSubmit={onSubmit} noValidate aria-busy={submitting}>
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          autoCapitalize="off"
          disabled={submitting}
          hint="At least 12 characters."
          error={newPasswordError}
          {...register('newPassword')}
        />

        <TextField
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          autoCapitalize="off"
          disabled={submitting}
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        <div
          role="alert"
          aria-live="polite"
          className="mb-3 min-h-5"
        >
          {submitErrorMessage !== undefined ? (
            <p
              className="text-sm"
              style={{ color: 'var(--fg-danger)' }}
            >
              {submitErrorMessage}
            </p>
          ) : null}
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          full
          disabled={submitting}
          aria-busy={submitting}
          aria-disabled={submitting}
          aria-label={submitting ? 'Resetting password' : 'Reset password'}
        >
          {submitting ? 'Resetting…' : 'Reset password'}
        </Button>

        <a
          href="/login"
          className="mt-4 inline-flex min-h-touch items-center justify-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          ← Back to sign in
        </a>
      </form>
    </>
  );
}

function SuccessSurface(): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          Password reset successfully
        </h1>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Your new password is saved. All active sessions have been
          signed out for your security — sign in with your new password
          to continue.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Sign in →
        </a>
      </section>
    </Card>
  );
}

function InvalidLinkSurface(): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          This reset link is not valid
        </h1>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          This password-reset link can no longer be used. Request a new
          one to continue.
        </p>
        <a
          href="/password-reset"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Request a new reset link →
        </a>
      </section>
    </Card>
  );
}
